/**
 * Award-data SSOT — SCHADS Award MA000100
 *
 * Social, Community, Home Care and Disability Services Industry Award.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ISOLATION GUARANTEE
 * ═══════════════════════════════════════════════════════════════════════
 *
 * This file encodes **MA000100-specific constants ONLY**. DO NOT add fields
 * from other awards' provisions even if they share field names. Cross-award
 * shape-porting risk per `project_cross_award_shape_porting_risk.md` is
 * mitigated by this isolation. See runbook §7
 * (`docs/operations/runbooks/award-engine-clause-provenance.md`).
 *
 * Empirical record: 4-of-4 non-Nurses awards have had Nurses-shape ports
 * proven defective (errata #16 / #19 + Phase 1J/1K/1L). The pattern is now
 * empirically structural, not occasional. Per-award files are the
 * isolation primitive.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * SOURCES + INDEXATION
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Existing provision constants cite these primary sources. Dated base hourly
 * schedules below cite catalogue migrations separately, with evidence limits:
 *   - FWC consolidated MA000100 (last updated 23 June 2026 per file)
 *   - FWO Pay Guide G01002983 (published 04/09/2025, effective 1 Oct 2025)
 *   - FWC determination PR791117 (SCHADS Stage 3, ppc 1 Oct 2025)
 *   - FWC determination PR786807 (SCHADS expense allowances, ppc 1 Jul 2025)
 *   - FWC determination PR737905 (SCHADS clause renumbering, ppc 1 Jul 2022)
 *
 * July 2026 fallback figures are copied literally from the nominated catalogue
 * migration; their pinned primary-source limits are recorded in the artifact.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * BASE HOURLY RATE VINTAGE
 * Dated snapshots below mirror the national catalogue. Compatibility exports
 * use July 2026, in force at the 2026-10-01 source cutover. December 2026
 * changes only HCD. Historical HCA coverage retains the original seed's
 * October-2025 backdating limitation. Allowance vintages remain separate.
 */

import type { OtRateBands, RecallRules, DisturbanceRules } from './_shared';

// ═══════════════════════════════════════════════════════════════════════
// Standard rate anchor (cl 3.1)
// ═══════════════════════════════════════════════════════════════════════

/**
 * SCHADS cl 3.1 "standard rate" — anchored to SACS L3.3 weekly minimum.
 *
 * Phase 1.4 Cluster E1 (2026-05-17) primary-source verification:
 *   cl 3.1 anchors the standard rate to **SACS L3.3 weekly minimum** =
 *   $1,543.37 (NOT SACS L3 PP3 $1,224.90, as Cluster D §5 D.1 claimed;
 *   Cluster E1 specialist primary-source verification reversed the claim).
 *
 * This constant is the ERO-loaded SACS L3.3 weekly wage used to derive
 * `SCHADS_STANDARD_HOURLY_RATE`. It is **not** the cl 3.1 standard rate
 * for wage-related allowance percentages. Those use the cl 15.3
 * **minimum** column at L3 pay point 3 — see
 * `SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE` in
 * `payroll/wage-related-allowances.ts`. Using this figure (or the
 * ERO-loaded L3 PP3 $1,616.71) inflates every percentage allowance.
 *
 * Source: FWC consolidated MA000100 cl 3.1 + Pay Guide G01002983 p.3 (SACS
 * L3.3 weekly rate) + PR791117 ppc 1 Oct 2025.
 * Verified: 2026-05-17 by aged-care-legislation specialist (Phase 1.4 Cluster E1).
 * Artifact: docs/operations/clause-verifications/2026-05-17-aged-care-legislation-schads-cluster-e1-regulatory-rates-numerics.md
 * Errata: #25 (Cluster D reversal — Cluster D claimed L3 PP3 $1,224.90).
 * Next review: AWR 2026 (22 Jun 2026 hard deadline).
 */
export const SCHADS_STANDARD_WEEKLY_RATE = 1_543.37;

/** SCHADS cl 3.1 standard hourly rate — derived $1,543.37 ÷ 38h. PR791117. */
export const SCHADS_STANDARD_HOURLY_RATE = 40.62;

// Dated base hourly rates — catalogue mirror (allowances are separate).

export interface SchadsRateSchedule {
  /** First day of the catalogue period, YYYY-MM-DD. */
  effectiveFrom: string;
  /** Exclusive end of the catalogue period, or null. */
  effectiveTo: string | null;
  levelRates: Record<string, number>;
  homeCareAgedRates: Record<string, number>;
  homeCareDisabilityRates: Record<string, number>;
}

/**
 * Historical fallback snapshot, retained for replay. SACS and HCD are July
 * 2025 vintage; HCA is October 2025 Stage 3. The original national seed
 * deliberately backdated that HCA snapshot; this preserves its coverage,
 * rather than claiming HCA Stage 3 was legally operative in July 2025.
 * Source: 20260601140000_seed_schads_v2.sql; FWO G01002983 pp.3,19.
 * Verified: 2026-10-01 by aged-care-legislation specialist (October guide).
 * Artifact: docs/operations/clause-verifications/2026-10-01-aged-care-legislation-schads-ts-rate-schedules.md
 * Next review: prior-period primary guide needed to resolve HCA backdating.
 */
const SCHADS_LEVEL_RATES_2025: Record<string, number> = {
  'L1.1': 26.3,
  'L1.2': 27.15,
  'L1.3': 28.12,
  'L2.1': 34.58,
  'L2.2': 35.67,
  'L2.3': 36.75,
  'L2.4': 37.73,
  'L3.1': 38.65,
  'L3.2': 39.77,
  'L3.3': 40.62,
  'L3.4': 41.45,
  'L4.1': 44.58,
  'L4.2': 45.75,
  'L4.3': 46.93,
  'L4.4': 47.97,
  'L5.1': 51.0,
  'L5.2': 52.1,
  'L5.3': 53.31,
  'L6.1': 55.72,
  'L6.2': 56.95,
  'L6.3': 58.19,
  'L7.1': 60.27,
  'L7.2': 61.53,
  'L7.3': 62.79,
  'L8.1': 65.39,
  'L8.2': 66.67,
  'L8.3': 67.96,
};

const SCHADS_HOME_CARE_AGED_RATES_2025: Record<string, number> = {
  HCA1: 31.13,
  HCA2: 32.86,
  HCA3: 34.59,
  HCA4: 35.97,
  HCA5: 37.35,
  HCA6: 38.74,
};

const SCHADS_HOME_CARE_DISABILITY_RATES_2025: Record<string, number> = {
  HCD1: 26.05,
  'HCD2.1': 27.55,
  'HCD2.2': 27.74,
  'HCD3.1': 28.12,
  'HCD3.2': 28.98,
  'HCD4.1': 30.67,
  'HCD4.2': 31.29,
  'HCD5.1': 32.89,
  'HCD5.2': 34.19,
};

/**
 * Source: national MA000100 catalogue, 20270820000000_awr_2026_1jul_reseed.sql.
 * Authority recorded there: PR799380 / AWR 2026; effective 2026-07-01.
 * Verified: 2026-10-01 catalogue parity; primary July PDF unavailable locally.
 * Artifact: docs/operations/clause-verifications/2026-10-01-aged-care-legislation-schads-ts-rate-schedules.md
 * Next review: AWR 2027 or a further determination.
 */
const SCHADS_LEVEL_RATES_2026: Record<string, number> = {
  'L1.1': 27.55,
  'L1.2': 28.44,
  'L1.3': 29.45,
  'L2.1': 36.22,
  'L2.2': 37.36,
  'L2.3': 38.5,
  'L2.4': 39.53,
  'L3.1': 40.49,
  'L3.2': 41.66,
  'L3.3': 42.55,
  'L3.4': 43.42,
  'L4.1': 46.7,
  'L4.2': 47.92,
  'L4.3': 49.16,
  'L4.4': 50.25,
  'L5.1': 53.43,
  'L5.2': 54.58,
  'L5.3': 55.85,
  'L6.1': 58.37,
  'L6.2': 59.66,
  'L6.3': 60.95,
  'L7.1': 63.13,
  'L7.2': 64.46,
  'L7.3': 65.77,
  'L8.1': 68.49,
  'L8.2': 69.84,
  'L8.3': 71.19,
};

/** July 2026 catalogue rows; provenance and artifact as for SACS above. */
const SCHADS_HOME_CARE_AGED_RATES_2026: Record<string, number> = {
  HCA1: 32.61,
  HCA2: 34.42,
  HCA3: 36.23,
  HCA4: 37.68,
  HCA5: 39.13,
  HCA6: 40.58,
};

/** July 2026 catalogue rows; provenance and artifact as for SACS above. */
const SCHADS_HOME_CARE_DISABILITY_RATES_2026: Record<string, number> = {
  HCD1: 27.28,
  'HCD2.1': 28.86,
  'HCD2.2': 29.06,
  'HCD3.1': 29.45,
  'HCD3.2': 30.36,
  'HCD4.1': 32.13,
  'HCD4.2': 32.77,
  'HCD5.1': 34.45,
  'HCD5.2': 35.81,
};

/**
 * Source: FWC PR814259, minimum weekly rates / 38, rounded to catalogue cents.
 * Catalogue: 20306010000000_schads_home_care_disability_interim_increase_1_dec_2026.sql.
 * Effective: 2026-12-01 (first full pay period qualification belongs to caller).
 * Verified: 2026-10-01 by aged-care-legislation specialist.
 * Artifact: docs/operations/clause-verifications/2026-10-01-aged-care-legislation-schads-ts-rate-schedules.md
 * Next review: AWR 2027 or a further determination.
 */
const SCHADS_HOME_CARE_DISABILITY_RATES_2026_12: Record<string, number> = {
  HCD1: 31.38,
  'HCD2.1': 33.19,
  'HCD2.2': 33.41,
  'HCD3.1': 33.87,
  'HCD3.2': 34.92,
  'HCD4.1': 36.95,
  'HCD4.2': 37.68,
  'HCD5.1': 39.62,
  'HCD5.2': 40.58,
};

export const SCHADS_RATE_SCHEDULES: readonly SchadsRateSchedule[] = [
  {
    effectiveFrom: '2025-07-01',
    effectiveTo: '2026-07-01',
    levelRates: SCHADS_LEVEL_RATES_2025,
    homeCareAgedRates: SCHADS_HOME_CARE_AGED_RATES_2025,
    homeCareDisabilityRates: SCHADS_HOME_CARE_DISABILITY_RATES_2025,
  },
  {
    effectiveFrom: '2026-07-01',
    effectiveTo: '2026-12-01',
    levelRates: SCHADS_LEVEL_RATES_2026,
    homeCareAgedRates: SCHADS_HOME_CARE_AGED_RATES_2026,
    homeCareDisabilityRates: SCHADS_HOME_CARE_DISABILITY_RATES_2026,
  },
  {
    effectiveFrom: '2026-12-01',
    effectiveTo: null,
    levelRates: SCHADS_LEVEL_RATES_2026,
    homeCareAgedRates: SCHADS_HOME_CARE_AGED_RATES_2026,
    homeCareDisabilityRates: SCHADS_HOME_CARE_DISABILITY_RATES_2026_12,
  },
];

/**
 * Stable no-date compatibility baseline, in force at this change's 2026-10-01
 * source cutover. Do not select the final schedule: December is still future.
 * Advance explicitly on a later rate refresh; no wall-clock-dependent exports.
 */
export const SCHADS_CURRENT_RATE_DATE = '2026-10-01';

/** Local business day; dates before our retained history use its first snapshot. */
export function schadsRateScheduleFor(date?: string | null): SchadsRateSchedule {
  const day = date == null ? SCHADS_CURRENT_RATE_DATE : String(date).slice(0, 10);
  const asAt = /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : SCHADS_CURRENT_RATE_DATE;
  return (
    [...SCHADS_RATE_SCHEDULES].reverse().find((row) => row.effectiveFrom <= asAt) ??
    (SCHADS_RATE_SCHEDULES[0] as SchadsRateSchedule)
  );
}

/** Compatibility exports: July 2026, the period in force on 2026-10-01. */
export const SCHADS_LEVEL_RATES: Record<string, number> = SCHADS_LEVEL_RATES_2026;
export const SCHADS_HOME_CARE_AGED_RATES: Record<string, number> = SCHADS_HOME_CARE_AGED_RATES_2026;
export const SCHADS_HOME_CARE_DISABILITY_RATES: Record<string, number> =
  SCHADS_HOME_CARE_DISABILITY_RATES_2026;

/** @deprecated ERO is already consolidated into the published SACS rates. */
export const SCHADS_SACS_ERO_RATES: Record<string, number> = { ...SCHADS_LEVEL_RATES };

// ═══════════════════════════════════════════════════════════════════════
// Penalty rates — permanent + casual
// ═══════════════════════════════════════════════════════════════════════

/**
 * Penalty rates for PERMANENT employees.
 *
 * Source: FWC consolidated MA000100 cl 26.1 (Saturday/Sunday) +
 *         cl 29.3 (shift loadings) + cl 34.2 (public holiday).
 * Verified: 2026-04-07 + Phase 1G (PH OT fix) + Phase 1L (citation corrections).
 *
 * Phase 1L citation corrections:
 *   - publicHoliday cited cl 26.3(a); actual cl 34.2(a). Math value 2.5 unchanged.
 *   - shift loadings cited cl 29.1; actual cl 29.3 (cl 29.1 is "Engagement in
 *     shiftwork", not loadings). Math values 1.125 / 1.15 unchanged.
 */
export const SCHADS_PERMANENT_PENALTY_RATES = {
  saturdayOrdinary: 1.5, // cl 26.1 — 150%
  sundayOrdinary: 2.0, // cl 26.1 — 200%
  publicHoliday: 2.5, // cl 34.2(a) — 250%
  afternoonShiftLoading: 1.125, // cl 29.3(a) — 12.5% afternoon shift loading
  nightAfter12am: 1.15, // cl 29.3(b) — 15% night shift loading
} as const;

/**
 * Penalty rates for CASUAL employees — ALL-INCLUSIVE of casual loading.
 *
 * SCHADS publishes all-inclusive casual rates directly in the Award text per
 * cl 26.4 (weekend) + cl 34.2(c)/(d) (PH). DO NOT add separate casual loading
 * when these rates are used.
 *
 * Phase 1L (2026-05-17) citation correction: pre-fix comment cited cl 26.1
 * casual / cl 26.3 casual — the actual all-inclusive casual rates are at
 * cl 26.4(a)/(b) (Sat/Sun) and cl 34.2(d) (PH). Cl 10.4 is the 25% casual
 * loading definition; cl 10.4(b) was renumbered by PR737905 ppc 01Jul22 to
 * just cl 10.4 (single paragraph). Math values unchanged.
 */
export const SCHADS_CASUAL_PENALTY_RATES = {
  saturdayOrdinary: 1.75, // cl 26.4(a): 175% inclusive of cl 10.4 25% loading
  sundayOrdinary: 2.25, // cl 26.4(b): 225% inclusive of cl 10.4 25% loading
  publicHoliday: 2.75, // cl 34.2(d): 275% inclusive of cl 10.4 25% loading
  afternoonShiftLoading: 1.375, // cl 29.3(a) + cl 10.4 casual: 12.5% + 25%
  nightAfter12am: 1.4, // cl 29.3(b) + cl 10.4 casual: 15% + 25%
} as const;

/** Casual loading — 25% per cl 10.4 (renumbered from cl 10.4(b) by PR737905 ppc 01Jul22). */
export const SCHADS_CASUAL_LOADING = 0.25;

// ═══════════════════════════════════════════════════════════════════════
// Overtime rates (cl 28.1)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Overtime rates (cl 28.1).
 *
 * The Pay Guide table column header (G01002983 page 4) reads:
 *   "Overtime - Monday to Saturday - first 3 hours (full-time) or
 *    first 2 hours (disability services and part-time)"
 *
 * Cutover from 1.5× to 2.0× depends on employment type:
 *   - Full-time SACS / Aged Care employees: first 3 hours at 1.5×, thereafter 2.0×
 *   - Part-time / casual / disability services: first 2 hours at 1.5×, thereafter 2.0×
 *
 * Sunday and public holiday overtime is 2.0× and 2.5× respectively for ALL
 * hours regardless of employment type.
 *
 * Phase 1G audit fix (2026-05-16): publicHolidayMultiplier was 2.0× pre-fix,
 * silently underpaying every PH OT hour. Audit-verified against FWC variation
 * to cl 28.1(a)(iv) + Pay Guide G01002983 PH section + multiple secondary
 * confirmations. All other engines (Aged Care, Nurses, HPSS) already hardcode
 * 2.5× — SCHADS was the only outlier. Wage-theft criminal regime live since
 * 1 Jan 2025 makes this fix not-deferable.
 *
 * Source: cl 28.1 + Pay Guide G01002983 p.4.
 * Verified: 2026-04-07 (initial) + 2026-05-16 (Phase 1G PH OT fix).
 */
export const SCHADS_OVERTIME_RATES = {
  initialMultiplier: 1.5,
  extendedMultiplier: 2.0,
  sundayMultiplier: 2.0, // cl 28.1(a)(iii) / cl 28.1(b)(i)
  publicHolidayMultiplier: 2.5, // cl 28.1(a)(iv) PH OT
  initialHoursFullTime: 3, // cl 28.1(a)(ii) FT SACS/crisis
  initialHoursPartTimeOrCasual: 2, // cl 28.1(a)(i) FT disability/home/day + cl 28.1(b)(i) PT/casual
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Broken shift allowance (cl 20.12)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Broken shift allowance per cl 20.12(a)/(b).
 *
 * Fair Work publishes these as FLAT dollar amounts per broken shift, not as
 * percentages of the standard rate. Use the published flat values directly.
 *
 * Source: Pay Guide G01002983 p.25 "Allowances" table.
 *   - cl 20.12(a) — Broken shift allowance, 1 unpaid break: $20.82
 *   - cl 20.12(b) — Broken shift allowance, 2 unpaid breaks: $27.56
 *
 * These amounts apply per broken shift, regardless of the worker's level/rate.
 */
export const SCHADS_BROKEN_SHIFT_FLAT = {
  oneBreak: 20.82,
  twoOrMoreBreaks: 27.56,
} as const;

/**
 * @deprecated Use `SCHADS_BROKEN_SHIFT_FLAT` — the Award publishes flat
 * per-shift dollar values, not percentages of weekly rate. Retained as a
 * percentage for one release cycle to support callers that derive from the
 * worker's own rate (which is wrong per the Award but was the previous
 * behaviour).
 */
export const SCHADS_BROKEN_SHIFT_RATES = {
  oneBreak: 0.017,
  twoOrMoreBreaks: 0.0225,
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Allowances
// ═══════════════════════════════════════════════════════════════════════

/**
 * SCHADS allowances — verified against FWC MA000100 allowance sheet
 * (operative 1 July 2025 — allowances are separate from the 1 Oct 2025
 * aged-care home-care worker wage uplift) via aged-care-legislation
 * specialist, 2026-04-18.
 *
 * Phase 1L (2026-05-17) — mealAllowance value triply-verified:
 *   - Cluster D §5 D.2 flagged $16.62 / cl 20.5 as wrong vs cl 20.10(a) $31.35.
 *   - Phase 1L FIX SHAPE specialist REVERSED Cluster D: cl 20.10 is "Board
 *     and lodging" (wage REDUCTION clause when employer provides accommodation,
 *     NOT a meal payment provision). cl 20.5 IS the delayed-meal allowance.
 *   - Cross-confirmed at cl 28.5(b) which explicitly cites "the meal
 *     allowance, as prescribed in clause 20.5".
 *   - Cluster E1 re-confirmed the value.
 *   Errata #24 catalogues the sweep-itself reversal.
 *
 * Jul 2025 vintage flats. From 1 July 2026 the constants path computes
 * wage-related fields (sleepover, on-call, first-aid, broken-shift) from
 * the cl 15.3 minimum-column standard rate — see
 * `payroll/wage-related-allowances.ts`. Expense/CPI fields stay here.
 *
 * Source: Pay Guide G01002983 p.25 + PR786807 (meal allowance ppc 1 Jul 2025)
 *         + PR786639 (sleepover allowance ppc 1 Jul 2025).
 * Verified: 2026-04-07 + Phase 1L (2026-05-17 mealAllowance) + Phase 1.4
 *           Cluster E1 (2026-05-17 sleepoverRate + on-call + broken-shift).
 */
export const SCHADS_ALLOWANCES = {
  travelPerKm: 0.99, // cl 20.7(a) — per km vehicle allowance
  sleepoverRate: 60.02, // cl 25.7(d) — sleepover allowance per night
  onCallWeekday: 24.5, // cl 20.11(a) — Mon-Fri 24h period
  onCallWeekend: 48.51, // cl 20.11(b) — Sat/Sun/PH 24h period
  mealAllowance: 16.62, // cl 20.5 — delayed-meal allowance (single flat amount, per occasion)
  firstAidWeekly: 20.46, // cl 20.6(a) — full-time per week
  firstAidHourlyPartTime: 0.54, // cl 20.6(b) — PT/casual per hour, capped at $20.46/wk
  uniformPerShift: 1.23, // cl 20.2(b) — per shift
  uniformWeekly: 6.24, // cl 20.2(b) — weekly cap
  laundryPerShift: 0.32, // cl 20.2(b) — per shift
  laundryWeeklyCap: 1.49, // cl 20.2(b) — weekly maximum
} as const;
// NOTE: the block above is at 1-Jul-2025 vintage. The 1-Jul-2026 corrections
// (travelPerKm 0.99→1.01, on-call 24.5/48.51→25.66/50.81, broken-shift + meal)
// are a PLATFORM-WIDE reseed tracked separately from the km-tax-split feature —
// it perturbs every live SCHADS provider (incl. Medea payroll) and must be
// sequenced around their pay-run window, not bundled with the split. See
// docs/operations/clause-verifications/2026-07-20-schads-vehicle-allowance-km-split-verification.md.

// ── Cents-per-km tax split (ATO PAYGW-exempt component vs taxable residual) ──
// The km allowance a provider PAYS (rates.allowances.travelPerKm, EBA/tenant
// override) is split for STP Phase 2 reporting into a PAYGW-exempt line (up to
// the ATO rate, ≤5,000 business km/FY) and a taxable residual line. Both report
// as STP allowance type 'CD' (cents per km); only the withheld flag differs.
// These are statutory/ATO values (platform rows), NOT the SCHADS award rate —
// they bind regardless of what a tenant pays per km.
/** ATO approved cents-per-km rate, income year 2026-27 (base 0.89 + one-off 0.02 uplift). PAYGW-exempt up to this rate. */
export const ATO_CENTS_PER_KM_2026_27 = 0.91;
/** SCHADS cl 20.7(a) vehicle allowance minimum, effective 1 Jul 2026 (PR799535). A configured paid rate below this underpays the award. */
export const SCHADS_VEHICLE_AWARD_FLOOR_2026_27 = 1.01;
/** ATO business-km exemption limit per employee per financial year. Beyond this, the WHOLE per-km payment (incl. the exempt component) becomes withholding-liable. */
export const SCHADS_KM_FY_EXEMPT_LIMIT = 5000;

// ═══════════════════════════════════════════════════════════════════════
// Leave entitlements
// ═══════════════════════════════════════════════════════════════════════

/**
 * Leave entitlements (NES + SCHADS).
 *
 * Phase 1L citation correction: annualLeaveLoading was cited cl 32.3 which
 * doesn't exist in MA000100; the 17.5% loading is at cl 31.4(a). Math
 * value unchanged.
 */
export const SCHADS_LEAVE = {
  annualLeaveDays: 20, // NES
  personalLeaveDays: 10, // NES
  longServiceLeaveWeeks: 8.667, // NSW LSL Act (10 years qualifying)
  annualLeaveLoading: 0.175, // cl 31.4(a) — 17.5%
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Minimum shift hours + roster limits
// ═══════════════════════════════════════════════════════════════════════

/**
 * Minimum shift hours by employment type.
 *
 * Phase 1.4 Cluster E1 (2026-05-17) specialist verification refined the
 * cl 10.5 reading:
 *   - cl 10.5(a) — 3h minimum for PART-TIME SACS / CASUAL SACS doing
 *     non-disability work. Does NOT apply to full-time SACS employees
 *     (full-time pays guaranteed 38h regardless of shift length).
 *   - cl 10.5(b) — 2h minimum for PART-TIME / CASUAL home care + community
 *     work + SACS doing disability work.
 *
 * The `fullTime: 3` value below has NO Award primary-source anchor as a
 * per-shift floor — full-time employees do not have a per-shift minimum
 * payment under cl 10.5. The constant is retained as a STATURA-internal
 * minimum-engagement default for the rostering preflight (emits a
 * MIN_ENGAGEMENT advisory issue), NOT as Award-mandated wage compliance.
 * Cluster E1 errata #26 catalogues the FT-shape mislabel.
 */
export const SCHADS_MINIMUM_SHIFT_HOURS = {
  partTime: 2, // cl 10.5(b) — 2h minimum for PT (home care + community)
  casual: 2, // cl 10.5(b) — 2h minimum for casual
  fullTime: 3, // Statura-internal rostering advisory (no Award per-shift minimum for FT)
} as const;

/**
 * Roster compliance limits.
 *
 * Phase 1.4 Cluster E1 (2026-05-17) specialist verification:
 *   - maxOrdinaryHoursPerWeek 38 — cl 25.1 ordinary hours
 *     (was cited "cl 22 ordinary hours framework" pre-Cluster-E1; cl 22
 *     is "Day worker" in current MA000100 — renumbering drift fixed).
 *   - maxOrdinaryHoursPerDay 10 — cl 25.1.
 *   - spreadOfHoursMax 12 — cl 25.6(f) broken-shift span maximum.
 *   - consecutiveDaysMax 5 — **NO primary-source anchor in MA000100**.
 *     cl 25.3 mandates 2 rest days per week (rest-days mandate, not work-
 *     days max). Constant retained as a STATURA-internal rostering
 *     fatigue/rest advisory. Cluster E1 errata #27 catalogues the
 *     fabrication.
 *   - breakAfterHours 5 — STATURA-internal break-cadence default (cl 27
 *     Meal breaks doesn't impose a strict "after 5h" rule).
 *   - mealBreakMinutes 30 — cl 27.1(a) Meal break (was cited "cl 24
 *     Breaks" pre-Cluster-E1; cl 24 is "Payment of wages" — renumbering
 *     drift fixed).
 *   - minimumBreakBetweenShifts 10 — cl 25.4(a) (Phase 1L correction).
 */
export const SCHADS_ROSTER_LIMITS = {
  maxOrdinaryHoursPerWeek: 38, // cl 25.1
  maxOrdinaryHoursPerDay: 10, // cl 25.1
  spreadOfHoursMax: 12, // cl 25.6(f)
  consecutiveDaysMax: 5, // Statura-internal fatigue advisory (no Award provision; cl 25.3 mandates 2 rest days)
  breakAfterHours: 5, // Statura-internal break-cadence default
  mealBreakMinutes: 30, // cl 27.1(a) Meal break
  minimumBreakBetweenShifts: 10, // cl 25.4(a)
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Recall-to-work rules (cl 28.4) — Phase 1L primary-source rewrite
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase 1L (Award Engine Integrity 2026-05-17) — SCHADS recall-to-work
 * rules per MA000100 cl 28.4 (verified primary-source against FWC
 * consolidated PDF).
 *
 * Corrects Phase 1C commit b2e8dcda which shipped `SCHADS_RECALL_RULES`
 * as a Nurses MA000034 cl 19.6/19.7 split-shape copy (3h in-person + 1h
 * electronic + travel-counts + within-3h carveout) with the clauseRef
 * `cl 25.7(b)` — wrong on TWO counts: (1) cl 25.7(b) is sleepover
 * refusal (not recall), and (2) the actual recall provision at cl 28.4
 * is UNIFIED 2h flat, not the Nurses split shape. This is the 4th
 * confirmed instance of cross-award shape-porting.
 *
 * Verbatim cl 28.4 (FWC consolidated MA000100, "Last updated 23 June 2026"):
 *
 *     "An employee who is recalled to work overtime after leaving the
 *      workplace and requested by their employer to attend a workplace
 *      in order to perform such overtime work will be paid for a
 *      minimum of two hours' work at the appropriate rate for each
 *      time recalled. If the work required is completed in less than
 *      two hours the employee will be released from duty."
 *
 *   - **2h flat minimum** at appropriate OT rate.
 *   - **No electronic-recall provision** — electronic input pays the 2h floor.
 *   - **No travel-counts-as-time-worked** — MA000100 has no equivalent of Nurses cl 19.7(c).
 *   - **No within-rostered-start carveout** — no equivalent of Nurses cl 19.7(d).
 *   - Casual loading is INCLUSIVE per cl 10.4 (cl 26.4 + cl 34.2(d)
 *     publish all-inclusive casual rates).
 *
 * Source: FWC consolidated MA000100 cl 28.4 + Phase 1L FIX SHAPE specialist.
 * Verified: 2026-05-17 (Phase 1L).
 * Artifact: docs/operations/clause-verifications/2026-05-17-aged-care-legislation-schads-math-defect-fix-shape.md
 * Errata: #19 (recall split-shape port + wrong clauseRef cl 25.7(b)).
 */
/**
 * Phase Codex Pass A PR #B (2026-05-19) — SCHADS MA000100 OT rate
 * bands for banded recall pay per cl 28.1 + cl 28.4.
 *
 * Verbatim cl 28.1 (FWC consolidated MA000100, specialist primary-
 * source verification 2026-05-19):
 *
 *   "28.1 Payment for overtime — full-time employees:
 *    (a) For full-time employees:
 *        (i)  Employees engaged in disability services, home care, or
 *             day-care services — Mon-Sat first 2 hours @ 150%, after
 *             2 hours @ 200%; Sunday @ 200%; PH @ 250%.
 *        (ii) Employees engaged in social and community services or
 *             crisis accommodation services — Mon-Sat first 3 hours
 *             @ 150%, after 3 hours @ 200%; Sunday @ 200%; PH @ 250%.
 *    (b) For part-time and casual employees:
 *        (i)  Mon-Sat first 2 hours @ 150%, after 2 hours @ 200%;
 *             Sunday @ 200%; PH @ 250%."
 *
 * **CRITICAL FINDING (specialist §4, 2026-05-19):** SCHADS FT social/
 * community/crisis-accommodation has a 3h tier-1 boundary
 * (cl 28.1(a)(ii)), NOT 2h. Disability / home care / day care FT plus
 * all PT plus all casual = 2h tier-1 boundary (cl 28.1(a)(i) /
 * cl 28.1(b)(i)). The engine must dispatch on `classificationStream`
 * AND `isFullTimeForOvertime` to select the correct boundary —
 * see `resolveSchadsTier1Hours` in `award-engines/recall.ts`.
 *
 * Saturday joins the Mon-Fri tier curve (cl 28.1 wording "Mon-Sat"
 * across both FT and PT/casual subclauses). NO Saturday flat carveout
 * (contrast AC cl 25.1(a)(i)(B)). DO NOT port the AC Saturday-flat
 * shape onto SCHADS.
 *
 * Casual loading is MULTIPLICATIVE per SCHADS settled doctrine via
 * FWC/FWO/multiple compliance guides (specialist artifact §5;
 * confidence MEDIUM but elevated to HIGH via cross-confirmation).
 * Casual tier-1 = 1.5 × 1.25 = 1.875; casual tier-2 = 2.0 × 1.25 = 2.5;
 * casual Sunday = 2.0 × 1.25 = 2.5; casual PH = 2.5 × 1.25 = 3.125.
 *
 * Source: FWC consolidated MA000100 cl 28.1 + cl 28.4 + Pay Guide
 *         G01002983 p.4 "Overtime - Monday to Saturday - first 3 hours
 *         (full-time) or first 2 hours (disability services and part-time)".
 * Verified: 2026-05-19 (Phase Codex Pass A PR #B specialist invocation;
 *           statura-regulatory-source-agent autonomous mode).
 * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
 * Errata: #34 (Codex P0-1/P0-2 amplification — pre-fix collapsed banded
 *         math to flat 1.5x AND missed stream-aware boundary).
 */
export const SCHADS_OT_RATE_BANDS: OtRateBands = {
  // weekdayTier1Hours: 2 is the default for disability/home-care/day-care
  // FT + PT + casual. Stream-aware dispatch in `resolveSchadsTier1Hours`
  // overrides to 3 for FT social/community/crisis-accommodation per
  // cl 28.1(a)(ii).
  weekdayTier1Hours: 2,
  weekdayTier1: {
    permanent: 1.5, // cl 28.1(a)(i) / (a)(ii) / (b)(i) Mon-Sat first N hours — 150%
    casual: 1.875, // cl 28.1(b)(i) casual + settled doctrine: 1.5 × 1.25
  },
  weekdayTier2: {
    permanent: 2.0, // cl 28.1(a)(i) / (a)(ii) / (b)(i) Mon-Sat after N hours — 200%
    casual: 2.5, // 2.0 × 1.25
  },
  // Saturday NOT flat — joins Mon-Fri tier curve per cl 28.1 "Mon-Sat".
  // saturdayFlat is intentionally omitted; do NOT port AC cl 25.1(a)(i)(B).
  sunday: {
    permanent: 2.0, // cl 28.1 Sunday — 200%
    casual: 2.5, // 2.0 × 1.25
  },
  publicHoliday: {
    permanent: 2.5, // cl 28.1 PH — 250% (FWC variation; matches PR791117 ppc 1 Oct 2025)
    casual: 3.125, // 2.5 × 1.25
  },
  clauseRefs: {
    weekdayTier1:
      'SCHADS Award MA000100 cl 28.4 recall trigger + cl 28.1(a)(i) FT disability/home-care/day-care + cl 28.1(b)(i) PT/casual (Mon-Sat OT first 2 hours — 150% / 187.5%); cl 28.1(a)(ii) FT social/community/crisis-accommodation (Mon-Sat OT first 3 hours — 150% / 187.5%)',
    weekdayTier2:
      'SCHADS Award MA000100 cl 28.4 recall trigger + cl 28.1(a)(i) / (a)(ii) / (b)(i) (Mon-Sat OT after tier-1 boundary — 200% / 250%)',
    sunday: 'SCHADS Award MA000100 cl 28.4 recall trigger + cl 28.1 (Sunday OT flat — 200% / 250%)',
    publicHoliday:
      'SCHADS Award MA000100 cl 28.4 recall trigger + cl 28.1 (PH OT flat — 250% / 312.5%)',
  },
};

export const SCHADS_RECALL_RULES: RecallRules = {
  awardCode: 'MA000100',
  inPersonMinimumHours: 2,
  electronicMinimumHours: 2,
  travelCountsAsTimeWorked: false,
  electronicRoundingMinutes: 15,
  withinRosteredStartCarveoutHours: 0,
  // Phase Codex Pass A PR #B (2026-05-19) — corrected from 'INCLUSIVE' to
  // 'MULTIPLICATIVE' per SCHADS settled-doctrine primary-source verification
  // (specialist artifact §4 + §5). 'INCLUSIVE' pre-fix silently underpaid
  // casuals by 25% on the OT multiplier.
  casualLoadingTreatment: 'MULTIPLICATIVE',
  otBands: SCHADS_OT_RATE_BANDS,
  clauseRef:
    'SCHADS Award MA000100 cl 28.4 (recall to work overtime — unified 2h floor; no in-person/electronic split, no travel-counts, no within-3h carveout) read with cl 28.1 banded OT rates (stream-aware FT tier-1 boundary)',
};

// ═══════════════════════════════════════════════════════════════════════
// Disturbance-during-sleepover rules (cl 25.7(e)) — Phase 1L citation fix
// ═══════════════════════════════════════════════════════════════════════

/**
 * Disturbance-during-sleepover rules per MA000100 cl 25.7(e).
 *
 * Phase 1L (2026-05-17) citation correction:
 *   Pre-Phase-1L cited cl 25.7(d)(iv) which does NOT exist in MA000100.
 *   The disturbance-during-sleepover provision is at cl 25.7(e):
 *
 *     "In the event of the employee on sleepover being required to perform
 *      work during the sleepover period, the employee will be paid for the
 *      time worked at the prescribed overtime rate with a minimum payment
 *      as for one hour worked."
 *
 *   Math (1h-minimum-at-OT) is correct against primary cl 25.7(e); only
 *   the cite was wrong. Errata #22 catalogues the citation drift.
 *
 *   Separate cl 25.7(f) 4h-flanking-shift minimum is NOT implemented in
 *   this engine — tracked as W12 post-Phase-1 workstream + CLAUDE.md
 *   known-gap (errata #21).
 *
 * Engine convention — 15-min rounding above the 1h minimum is NOT in the
 * MA000100 cl 25.7(e) verbatim text. Conservative-correct (rounds in
 * worker's favour); flagged in Phase 1.4 Cluster D as engine-convention-
 * not-Award-text.
 *
 * Source: FWC consolidated MA000100 cl 25.7(e).
 * Verified: 2026-05-17 (Phase 1L).
 */
export const SCHADS_DISTURBANCE_RULES: DisturbanceRules = {
  awardCode: 'MA000100',
  model: 'PER_EVENT',
  minimumHours: 1,
  roundingMinutes: 15,
  casualLoadingTreatment: 'INCLUSIVE',
  clauseRef: 'SCHADS Award MA000100 cl 25.7(e)',
};
